import {
  TRANSFER_ACTION_NAMES,
  type TransferActionName,
  type TransferPickOp,
  type TransferProblemKind,
  type TransferReceiveLine,
  type TransferRecord,
  type TransferRecordEvent,
  type TransferRecordHeader,
  type TransferRecordLine,
  type TransferRecordSerial,
  type TransferRecordShipment,
  type TransferStopProgress,
} from '@/data/transferRecord';
import type { TransferLineRow } from '@/data/transfers';
import {
  describeStatus,
  formatDaysLate,
  formatSiteClock,
  formatSiteDateTime,
  formatSiteDay,
  formatSiteDayWord,
  formatSiteDeadline,
  formatSiteMoment,
  fromSiteInputValue,
  siteDaysFromToday,
  siteZoneAbbreviation,
  toSiteInputValue,
  pluralize,
  sentenceCaseStatus,
  trackerExceptionLabel,
  trackerStepKey,
  trackerSteps,
  type PackingListInput,
  type ScanCountLine,
  type ScanCountProblemKind,
  type ScanCountSubmission,
  type StatusTone,
} from '@/domain/warehouse';
import type { GroupedTableGroup } from '../../../components/common/GroupedDataTable';
import type { LifecycleStageStep } from '../../../components/LifecycleStageTracker';
import { getTransferLinePhysicalGuard, type TransferLinePhysicalGuard } from '../transferLinePhysicalGuard';

/**
 * Pure view logic for the transfer record (no React, no network): labels, the one primary action,
 * the next-step banner, the tracker, line grouping, the Receive/Pick dialog adapters, shipment and
 * stop wording, site-time formatting and the packing list's input.
 */

export const TRANSFER_SECTIONS = ['lines', 'route', 'events', 'chat', 'history'] as const;
export type TransferSection = typeof TRANSFER_SECTIONS[number];

/** Old section paths still land (notifications store them). */
const LEGACY_SECTIONS: { readonly [segment: string]: TransferSection } = {
  overview: 'lines',
  schedule: 'lines',
  items: 'lines',
  waypoints: 'route',
  shipments: 'route',
  linked: 'events',
  timeline: 'history',
};

/**
 * Reads the path segment after `/transfers/:id/`: a known section, a legacy one to redirect, or
 * anything else (missing or unknown), which lands on Lines.
 */
export function resolveTransferSection(segment: string | null | undefined): { section: TransferSection; redirect: boolean } {
  const value = String(segment ?? '').split('/')[0].trim().toLowerCase();
  if ((TRANSFER_SECTIONS as readonly string[]).includes(value)) return { section: value as TransferSection, redirect: false };
  return { section: LEGACY_SECTIONS[value] ?? 'lines', redirect: true };
}

export function isR2Record(record: TransferRecord): boolean {
  return record.transfer.source === 'r2';
}

// ---------------------------------------------------------------------------------------------
// What the viewer may do (the reader's actions, with their reasons in words)
// ---------------------------------------------------------------------------------------------

export type TransferActionView = { allowed: boolean; reason: string | null };
export type TransferActionViews = { [name in TransferActionName]: TransferActionView };

const FROM_SIDE: ReadonlySet<TransferActionName> = new Set(['confirm', 'pick', 'ship', 'cancel', 'edit_lines', 'edit_dates']);
const TO_SIDE: ReadonlySet<TransferActionName> = new Set(['receive', 'create_return']);
const PRE_SHIP = new Set(['draft', 'confirmed', 'partially_picked', 'picked']);

function wrongStatusReason(name: TransferActionName, status: string): string {
  if (status === 'cancelled') return 'This transfer is cancelled.';
  switch (name) {
    case 'confirm':
      return 'Only a draft can be confirmed.';
    case 'pick':
      return status === 'draft' ? 'Confirm the transfer before picking.' : 'Picking is over: this transfer has shipped.';
    case 'ship':
      return status === 'draft' ? 'Confirm and pick the transfer before shipping.' : 'This transfer has already shipped.';
    case 'receive':
      return PRE_SHIP.has(status) ? 'Receiving opens once the transfer ships.' : 'Everything on this transfer is received.';
    case 'cancel':
      return 'A transfer that has shipped cannot be cancelled.';
    case 'edit_lines':
    case 'edit_dates':
      return 'Lines and dates can change only before picking starts.';
    case 'create_return':
      return status === 'draft' ? 'Confirm the transfer first; its return leg is made then.' : 'This transfer is finished.';
    default:
      return 'This is not available right now.';
  }
}

/** One reason key from the reader (or the server's own words) as a sentence. */
export function describeActionReason(record: TransferRecord, name: TransferActionName, reason: string | null): string {
  const from = siteName(record.transfer.fromSite, 'the shipping site');
  const to = siteName(record.transfer.toSite, 'the receiving site');
  switch (reason) {
    case 'read_only':
      return 'You are previewing another role, so nothing can be changed here.';
    case 'no_permission':
      return 'Your role can see transfers but not change them.';
    case 'r2_managed':
      return 'R2 manages this transfer, so this happens in R2.';
    case 'not_your_site':
      if (FROM_SIDE.has(name)) return `Only staff at ${from} (or an admin) can do this.`;
      if (TO_SIDE.has(name)) return `Only staff at ${to} (or an admin) can do this.`;
      return `Only staff at ${from} or ${to} (or an admin) can do this.`;
    case 'wrong_status':
      return wrongStatusReason(name, record.transfer.status);
    case 'no_lines':
      return 'Add at least one line before confirming.';
    case 'picking_started':
      return 'Picking has started, so the lines and dates are locked.';
    case 'fully_picked':
      return 'A line is fully picked, and a fully picked line cannot be put back, so the transfer cannot be cancelled. Ship it, or ask an admin.';
    case 'is_return_leg':
      return 'This transfer is itself a return leg.';
    case 'permanent':
      return 'A permanent transfer does not come back, so it has no return leg.';
    case 'already_has_return':
      return 'This transfer already has its return leg.';
    case null:
    case '':
    case 'not_allowed':
      return 'This is not available right now.';
    default:
      // A reason the server already wrote out; keys never contain spaces.
      return /\s/.test(reason) ? reason : 'This is not available right now.';
  }
}

export function describeTransferActions(record: TransferRecord): TransferActionViews {
  const views = {} as TransferActionViews;
  for (const name of TRANSFER_ACTION_NAMES) {
    const state = record.actions[name];
    views[name] = { allowed: state.allowed, reason: state.allowed ? null : describeActionReason(record, name, state.reason) };
  }
  return views;
}

/** The physical lock on a line (picked or moved units), from the record's line. */
export function linePhysicalGuard(line: TransferRecordLine): TransferLinePhysicalGuard {
  const row: TransferLineRow & { serials: Array<{ status: string }> } = {
    transfer_line_id: line.id,
    transfer_id: '',
    qty: line.qty,
    status: line.status as TransferLineRow['status'],
    shipped_qty: line.shippedQty,
    received_qty: line.receivedQty,
    kept_qty: 0,
    serials: line.serials
      .filter((serial) => serial.status !== 'assigned' && serial.status !== 'cancelled')
      .map((serial) => ({ status: serial.status })),
  };
  const guard = getTransferLinePhysicalGuard(row);
  if (guard.locked || line.pickedQty <= 0) return guard;
  // A partly counted quantity line has no serial evidence; its picked units are the floor.
  return {
    locked: true,
    protectedQuantity: line.pickedQty,
    message: guard.message ?? 'Picked or moved inventory cannot be deleted, reduced, or changed to another item.',
  };
}

/** "024-Columbus Base Office" → "Columbus" (the header's "Receive at Columbus"). */
export function shortSiteName(name: string | null | undefined): string {
  const full = String(name ?? '').trim();
  if (!full) return 'the receiving site';
  const withoutCode = full.replace(/^[A-Za-z0-9]{2,6}\s*-\s*/, '').trim();
  const withoutKind = withoutCode.replace(/\s+(?:base\s+office|base|office|warehouse)$/i, '').trim();
  return withoutKind || withoutCode || full;
}

export function siteName(site: { name: string | null } | null | undefined, fallback: string): string {
  return site?.name?.trim() || fallback;
}

// ---------------------------------------------------------------------------------------------
// Site time (domain/warehouse/siteTime.ts; New transfer uses it too). The record keeps these names.
// ---------------------------------------------------------------------------------------------

export {
  formatSiteClock,
  formatSiteDateTime,
  formatSiteDay,
  formatSiteDayWord,
  formatSiteDeadline,
  formatSiteMoment,
  fromSiteInputValue,
  siteDaysFromToday,
  siteZoneAbbreviation,
  toSiteInputValue,
};

// ---------------------------------------------------------------------------------------------
// Lines
// ---------------------------------------------------------------------------------------------

export function lineDisplayName(line: TransferRecordLine): string {
  return line.item?.name
    ?? line.product?.name
    ?? line.vehicle?.name
    ?? line.description
    ?? 'Unnamed line';
}

export function lineSku(line: TransferRecordLine): string | null {
  return line.item?.code ?? line.product?.code ?? null;
}

const RANGE_CODE = /^(.*?)(\d+)$/;

/** "RW-2201 to RW-2208" for a consecutive run, else the first few codes and "+N more". */
export function serialSummary(codes: readonly string[], max = 4): string | null {
  const list = codes.map((code) => code.trim()).filter(Boolean);
  if (!list.length) return null;
  if (list.length === 1) return list[0];
  const parsed = list.map((code) => code.match(RANGE_CODE));
  if (parsed.every((match) => match != null)) {
    const matches = parsed as RegExpMatchArray[];
    const prefix = matches[0][1];
    const width = matches[0][2].length;
    if (matches.every((match) => match[1] === prefix && match[2].length === width)) {
      const numbers = matches.map((match) => Number(match[2])).sort((left, right) => left - right);
      const consecutive = numbers.every((value, index) => index === 0 || value === numbers[index - 1] + 1);
      if (consecutive) {
        const pad = (value: number) => String(value).padStart(width, '0');
        return `${prefix}${pad(numbers[0])} to ${prefix}${pad(numbers[numbers.length - 1])}`;
      }
    }
  }
  if (list.length <= max) return list.join(', ');
  return `${list.slice(0, max).join(', ')} +${list.length - max} more`;
}

/** The units a line carries (cancelled ones left out). */
export function lineSerialCodes(line: TransferRecordLine): string[] {
  return line.serials.filter((serial) => serial.status !== 'cancelled').map((serial) => serial.code);
}

/** The second line under a line's SKU: its serials, or how it is counted. */
export function lineTrackingNote(line: TransferRecordLine): string {
  const serials = serialSummary(lineSerialCodes(line));
  if (serials) return serials;
  if (line.kind === 'vehicle') return 'Vehicle';
  if (line.kind === 'accessory') return 'Accessory';
  if (line.kind === 'external') return 'Not in the catalog';
  if (line.tracking === 'serial') return `${pluralize(line.qty, 'unit')} by serial`;
  return 'By quantity';
}

export const OTHER_CATEGORY = 'Other';

export function lineCategory(line: TransferRecordLine): string {
  return line.category?.trim() || OTHER_CATEGORY;
}

/** Lines grouped by category (in order of first appearance) with the table's group headers. */
export function groupLinesByCategory(lines: readonly TransferRecordLine[]): {
  lines: TransferRecordLine[];
  groups: GroupedTableGroup[];
} {
  const order: string[] = [];
  const byCategory = new Map<string, TransferRecordLine[]>();
  for (const line of lines) {
    const category = lineCategory(line);
    let list = byCategory.get(category);
    if (!list) {
      list = [];
      byCategory.set(category, list);
      order.push(category);
    }
    list.push(line);
  }
  return {
    lines: order.flatMap((category) => byCategory.get(category) ?? []),
    groups: order.map((category) => ({ key: category, label: category, count: byCategory.get(category)?.length ?? 0 })),
  };
}

/** Whether the lines are being received (anything shipped) or still picked. */
export type LineStage = 'pick' | 'receive';

const RECEIVE_STAGE_STATUSES = new Set(['partially_shipped', 'shipped', 'in_transit', 'partially_received', 'received', 'complete']);

export function lineStage(record: TransferRecord): LineStage {
  return RECEIVE_STAGE_STATUSES.has(record.transfer.status) || record.totals.shipped > 0 ? 'receive' : 'pick';
}

export type LineFilter = 'all' | 'open' | 'done';

export function lineIsDone(line: TransferRecordLine, stage: LineStage): boolean {
  if (line.status === 'cancelled') return true;
  if (stage === 'receive') return line.shippedQty > 0 ? line.receivedQty >= line.shippedQty : line.receivedQty >= line.qty;
  return line.pickedQty >= line.qty;
}

export function filterLines(lines: readonly TransferRecordLine[], filter: LineFilter, stage: LineStage): TransferRecordLine[] {
  if (filter === 'all') return [...lines];
  return lines.filter((line) => (filter === 'done') === lineIsDone(line, stage));
}

export function lineFilterLabels(stage: LineStage): { open: string; done: string } {
  return stage === 'receive' ? { open: 'Not received', done: 'Received' } : { open: 'Not picked', done: 'Picked' };
}

/** Lines that can still be received (shipped and not all in). */
export function receivableLines(lines: readonly TransferRecordLine[]): TransferRecordLine[] {
  return lines.filter((line) => line.status !== 'cancelled' && line.shippedQty > line.receivedQty);
}

// ---------------------------------------------------------------------------------------------
// Lifecycle: the one primary action, the next-step banner and the tracker
// ---------------------------------------------------------------------------------------------

export type TransferLifecycleAction = 'confirm' | 'pick' | 'ship' | 'receive';

export type TransferPrimaryAction = {
  action: TransferLifecycleAction;
  /** Header, melt bar and banner wording ("Receive at Columbus"). */
  label: string;
  /** Phone action bar wording ("Receive 22 units"). */
  phoneLabel: string;
  allowed: boolean;
  reason: string | null;
};

export function unitsLeftToPick(record: TransferRecord): number {
  return Math.max(0, record.totals.units - record.totals.picked);
}

export function unitsLeftToReceive(record: TransferRecord): number {
  return Math.max(0, record.totals.shipped - record.totals.received);
}

/** The one lifecycle action a Portal transfer offers now, or null (R2, finished, cancelled). */
export function primaryActionFor(record: TransferRecord): TransferPrimaryAction | null {
  if (isR2Record(record)) return null;
  const { status, toSite } = record.transfer;
  const make = (action: TransferLifecycleAction, label: string, phoneLabel: string): TransferPrimaryAction => {
    const state = record.actions[action];
    return {
      action,
      label,
      phoneLabel,
      allowed: state.allowed,
      reason: state.allowed ? null : describeActionReason(record, action, state.reason),
    };
  };
  switch (status) {
    case 'draft':
      return make('confirm', 'Confirm transfer', 'Confirm transfer');
    case 'confirmed':
      return make('pick', 'Start picking', `Pick ${pluralize(unitsLeftToPick(record), 'unit')}`);
    case 'partially_picked':
      return make('pick', 'Continue picking', `Pick ${pluralize(unitsLeftToPick(record), 'unit')}`);
    case 'picked':
      return make('ship', `Ship ${pluralize(record.totals.picked, 'unit')}`, `Ship ${pluralize(record.totals.picked, 'unit')}`);
    case 'partially_shipped':
    case 'shipped':
    case 'in_transit':
      return make('receive', `Receive at ${shortSiteName(toSite?.name)}`, `Receive ${pluralize(unitsLeftToReceive(record), 'unit')}`);
    case 'partially_received':
      return make('receive', 'Receive the rest', `Receive ${pluralize(unitsLeftToReceive(record), 'unit')}`);
    default:
      return null;
  }
}

/** "Ship 12 of 22": shipping what is picked while picking is unfinished, after an explicit confirmation. */
export type TransferShipShort = { label: string; picked: number; ordered: number; allowed: boolean; reason: string | null };

export function shipShortFor(record: TransferRecord): TransferShipShort | null {
  if (isR2Record(record) || record.transfer.status !== 'partially_picked' || record.totals.picked <= 0) return null;
  const { picked, units } = record.totals;
  const state = record.actions.ship;
  return {
    label: `Ship ${picked} of ${units}`,
    picked,
    ordered: units,
    allowed: state.allowed,
    reason: state.allowed ? null : describeActionReason(record, 'ship', state.reason),
  };
}

export type TransferNextStep = {
  tone: Extract<StatusTone, 'info' | 'ok' | 'warn'>;
  /** Bold first sentence. */
  lead: string | null;
  text: string;
};

/** The banner under a Portal transfer's header; R2 transfers show the R2 banner instead. */
export function nextStepFor(record: TransferRecord, now: Date = new Date()): TransferNextStep | null {
  if (isR2Record(record)) return null;
  const { transfer, totals } = record;
  const from = siteName(transfer.fromSite, 'the shipping site');
  const to = siteName(transfer.toSite, 'the receiving site');
  const toZone = transfer.toSite?.timezone ?? null;
  const fromZone = transfer.fromSite?.timezone ?? null;
  const needBy = formatSiteDateTime(transfer.needByAt, toZone, now);
  switch (transfer.status) {
    case 'draft':
      return totals.lineCount > 0
        ? { tone: 'info', lead: 'Draft.', text: `Check the lines, then confirm so ${from} can pick ${pluralize(totals.units, 'unit')}.` }
        : { tone: 'info', lead: 'Draft.', text: 'Add the items to move, then confirm the transfer.' };
    case 'confirmed': {
      const confirmed = formatSiteDay(transfer.confirmedAt, fromZone, now);
      return {
        tone: 'info',
        lead: confirmed ? `Confirmed ${confirmed}.` : 'Confirmed.',
        text: `${from} picks ${pluralize(totals.units, 'unit')}${needBy ? `; ${to} needs them by ${needBy}` : ''}. Scan each serial, or count the lines without serials.`,
      };
    }
    case 'partially_picked':
      return {
        tone: 'info',
        lead: `${totals.picked} of ${pluralize(totals.units, 'unit')} picked.`,
        text: 'Keep picking, or ship what is picked and record the rest as short.',
      };
    case 'picked':
      return {
        tone: 'info',
        lead: `All ${pluralize(totals.units, 'unit')} are picked.`,
        text: `Ship when it leaves ${from}. Add the carrier or truck so ${to} knows what to expect.`,
      };
    case 'partially_shipped':
    case 'shipped':
    case 'in_transit': {
      const late = formatDaysLate(transfer.daysLate);
      const arrives = formatSiteDayWord(transfer.expectedReceiveAt, toZone, now);
      const lead = late
        ? `${arrives ? `Expected ${arrives}` : 'Expected'} at ${to}, ${late}.`
        : arrives
          ? `Arrives ${arrives} at ${to}.`
          : `On the way to ${to}.`;
      return {
        tone: late ? 'warn' : 'ok',
        lead,
        text: 'Scan each case or serial to receive, or count the lines without serials.',
      };
    }
    case 'partially_received':
      return {
        tone: 'warn',
        lead: `${totals.received} of ${pluralize(totals.shipped, 'unit')} received at ${to}.`,
        text: 'Receive the rest when it arrives, or report what is missing.',
      };
    case 'received': {
      const received = formatSiteDay(transfer.actualReceiveAt, toZone, now);
      const leg = transfer.returnLeg;
      const ships = leg ? formatSiteDay(leg.expectedShipAt ?? transfer.expectedReturnAt, toZone, now) : null;
      const legName = leg?.docNo ? `Return leg ${leg.docNo}` : 'The return leg';
      return {
        tone: 'ok',
        lead: received ? `Received at ${to} ${received}.` : `Received at ${to}.`,
        text: leg && transfer.scope === 'temporary'
          ? `${legName} brings it back${ships ? `, shipping ${ships}` : ''}.`
          : 'Everything on this transfer is in.',
      };
    }
    case 'complete':
      return { tone: 'ok', lead: 'Complete.', text: transfer.scope === 'temporary' ? 'Everything went out and came back.' : 'Everything arrived.' };
    case 'cancelled':
      return { tone: 'warn', lead: 'Cancelled.', text: 'Nothing on this transfer moves. History keeps what happened.' };
    default:
      return null;
  }
}

/** Steps for LifecycleStageTracker with the board's detail lines ("Oct 5", "22 of 22"). */
export function trackerFor(record: TransferRecord, now: Date = new Date()): {
  steps: LifecycleStageStep<string>[];
  currentKey: string | null;
  exceptionLabel: string | null;
  statusLabel: string;
} {
  const { transfer, totals } = record;
  const fromZone = transfer.fromSite?.timezone ?? null;
  const toZone = transfer.toSite?.timezone ?? null;
  const detailFor = (key: string): string | null => {
    switch (key) {
      case 'confirmed':
        return formatSiteDay(transfer.confirmedAt, fromZone, now);
      case 'picked':
        return transfer.status === 'draft' ? null : `${totals.picked} of ${totals.units}`;
      case 'shipped':
        return formatSiteDay(transfer.actualShipAt, fromZone, now);
      case 'in_transit': {
        if (!transfer.expectedReceiveAt || transfer.actualReceiveAt) return null;
        const word = formatSiteDayWord(transfer.expectedReceiveAt, toZone, now);
        return word ? `Arrives ${word}` : null;
      }
      case 'received':
        return totals.shipped > 0 || ['received', 'partially_received', 'complete'].includes(transfer.status)
          ? `${totals.received} of ${totals.shipped || totals.units}`
          : null;
      default:
        return null;
    }
  };
  return {
    steps: trackerSteps('transfer').map((step) => ({
      key: step.key,
      label: step.label,
      targetStatus: null,
      detail: detailFor(step.key),
    })),
    currentKey: trackerStepKey('transfer', transfer.status),
    exceptionLabel: trackerExceptionLabel('transfer', transfer.status),
    statusLabel: describeStatus('transfer', transfer.status).label,
  };
}

/** "Temporary · back Oct 14", "Temporary" or "Permanent". */
export function scopeLabel(transfer: TransferRecordHeader, now: Date = new Date()): string {
  if (transfer.scope === 'permanent') return 'Permanent';
  const back = formatSiteDay(transfer.expectedReturnAt, transfer.toSite?.timezone ?? null, now);
  return back ? `Temporary · back ${back}` : 'Temporary';
}

// ---------------------------------------------------------------------------------------------
// Receive and Pick dialog adapters (components/receiving/ScanCountDialog)
// ---------------------------------------------------------------------------------------------

/** Prefix for a unit picked by its code (no asset id known yet); the server resolves it. */
const CODE_UNIT_PREFIX = 'code:';

function lineCodes(line: TransferRecordLine): string[] {
  return [line.item?.code, line.product?.code].filter((code): code is string => Boolean(code));
}

/** A unit's other codes (serial numbers, RFID tag): a scan of any of them matches its chip. */
function unitCodes(serial: TransferRecordSerial): { codes: string[]; label?: string } {
  const codes = serial.codes.filter((code) => code !== serial.code);
  return codes.length ? { codes, label: codes.join(' · ') } : { codes };
}

/** Lines to receive: shipped units not yet in. Serial chips carry asset ids (what the server takes). */
export function buildReceiveLines(lines: readonly TransferRecordLine[], onlyLineIds?: readonly string[] | null): ScanCountLine[] {
  const only = onlyLineIds && onlyLineIds.length ? new Set(onlyLineIds) : null;
  return lines
    .filter((line) => line.status !== 'cancelled' && line.shippedQty > 0 && (!only || only.has(line.id)))
    .map((line): ScanCountLine => {
      const base = {
        id: line.id,
        name: lineDisplayName(line),
        sku: lineSku(line) ?? undefined,
        expected: line.shippedQty,
        alreadyDone: Math.min(line.receivedQty, line.shippedQty),
      };
      if (line.tracking !== 'serial') return { ...base, tracking: 'quantity', codes: lineCodes(line) };
      return {
        ...base,
        tracking: 'serial',
        serials: line.serials
          .filter((serial) => serial.assetId && ['shipped', 'in_transit', 'received', 'kept'].includes(serial.status))
          .map((serial) => ({
            id: serial.assetId as string,
            code: serial.code,
            ...unitCodes(serial),
            done: serial.status === 'received' || serial.status === 'kept',
          })),
      };
    });
}

/** The submission as `transfer_receive` lines. */
export function receiveLinesFromSubmission(submission: ScanCountSubmission): TransferReceiveLine[] {
  return submission.map((entry): TransferReceiveLine => (entry.serialIds
    ? { line_id: entry.lineId, asset_ids: [...entry.serialIds] }
    : { line_id: entry.lineId, qty: entry.qty ?? 0 }));
}

/** Lines to pick. Pre-assigned units show as chips; other units of the item are found by lookup. */
export function buildPickLines(lines: readonly TransferRecordLine[]): ScanCountLine[] {
  return lines
    .filter((line) => line.status !== 'cancelled' && line.qty > 0)
    .map((line): ScanCountLine => {
      const base = {
        id: line.id,
        name: lineDisplayName(line),
        sku: lineSku(line) ?? undefined,
        expected: line.qty,
        alreadyDone: Math.min(line.pickedQty, line.qty),
      };
      if (line.tracking !== 'serial') return { ...base, tracking: 'quantity', codes: lineCodes(line) };
      return {
        ...base,
        tracking: 'serial',
        serials: line.serials
          .filter((serial) => serial.status !== 'cancelled')
          .map((serial) => ({
            id: serial.assetId ?? `${CODE_UNIT_PREFIX}${serial.code}`,
            code: serial.code,
            ...unitCodes(serial),
            done: serial.status !== 'assigned',
          })),
      };
    });
}

export type PickPlan = {
  ops: TransferPickOp[];
  /** The submitted line each operation came from. */
  opLineIds: string[];
};

/** The submission as `transfer_pick` operations: quantities are absolute, serials one per unit. */
export function pickPlanFromSubmission(submission: ScanCountSubmission, pickLines: readonly ScanCountLine[]): PickPlan {
  const byId = new Map(pickLines.map((line) => [line.id, line]));
  const ops: TransferPickOp[] = [];
  const opLineIds: string[] = [];
  for (const entry of submission) {
    if (entry.serialIds) {
      for (const unitId of entry.serialIds) {
        ops.push(unitId.startsWith(CODE_UNIT_PREFIX)
          ? { code: unitId.slice(CODE_UNIT_PREFIX.length), line_id: entry.lineId }
          : { asset_id: unitId, line_id: entry.lineId });
        opLineIds.push(entry.lineId);
      }
    } else if (entry.qty != null) {
      const alreadyDone = byId.get(entry.lineId)?.alreadyDone ?? 0;
      ops.push({ line_id: entry.lineId, qty: alreadyDone + entry.qty });
      opLineIds.push(entry.lineId);
    }
  }
  return { ops, opLineIds };
}

/** The dialog's problem kinds as the server's. */
export function problemKindFor(kind: ScanCountProblemKind): TransferProblemKind {
  return kind === 'extra' ? 'extra_item' : kind;
}

// ---------------------------------------------------------------------------------------------
// Shipments and stops
// ---------------------------------------------------------------------------------------------

export const SHIPMENT_MODES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'ground', label: 'Ground' },
  { value: 'standard', label: 'Standard' },
  { value: 'express', label: 'Express' },
  { value: 'overnight', label: 'Overnight' },
  { value: '2day', label: '2-day' },
  { value: '3day', label: '3-day' },
  { value: 'air', label: 'Air' },
  { value: 'other', label: 'Other' },
];

export const CARRIER_SUGGESTIONS = ['ITA vehicle', 'UPS', 'FedEx', 'USPS', 'DHL', 'Freight'] as const;

export function shipmentModeLabel(mode: string | null | undefined): string | null {
  if (!mode) return null;
  return SHIPMENT_MODES.find((entry) => entry.value === mode)?.label ?? sentenceCaseStatus(mode);
}

/** "UPS Ground", "ITA vehicle" or "Shipment". */
export function shipmentTitle(shipment: TransferRecordShipment): string {
  const carrier = shipment.carrier?.trim() || (shipment.vehicle || shipment.driverName ? 'ITA vehicle' : null);
  const mode = shipmentModeLabel(shipment.mode);
  return [carrier, mode].filter(Boolean).join(' ') || 'Shipment';
}

/** "Box truck 14 · driver M. Ortiz · left Oct 6, 4:10 PM". */
export function shipmentDetail(shipment: TransferRecordShipment, fromZone: string | null, toZone: string | null, now: Date = new Date()): string {
  const parts: string[] = [];
  if (shipment.vehicle?.name) parts.push(shipment.vehicle.name);
  if (shipment.driverName) parts.push(`driver ${shipment.driverName}`);
  const delivered = formatSiteDateTime(shipment.deliveredAt, toZone, now);
  const shipped = formatSiteDateTime(shipment.shippedAt, fromZone, now);
  if (delivered) parts.push(`delivered ${delivered}`);
  else if (shipped) parts.push(`left ${shipped}`);
  if (shipment.notes) parts.push(shipment.notes);
  return parts.join(' · ');
}

/** A carrier's public tracking page, for the carriers whose links are stable. */
export function trackingUrl(carrier: string | null | undefined, trackingNo: string | null | undefined): string | null {
  const number = String(trackingNo ?? '').replace(/\s+/g, '');
  if (!number) return null;
  const name = String(carrier ?? '').trim().toLowerCase();
  const encoded = encodeURIComponent(number);
  if (name.includes('ups') || /^1z/i.test(number)) return `https://www.ups.com/track?tracknum=${encoded}`;
  if (name.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${encoded}`;
  if (name.includes('usps')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encoded}`;
  if (name.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${encoded}`;
  return null;
}

/** How the transfer travels, for the header: the logistics mode and the latest shipment. */
export function logisticsSummary(record: TransferRecord): string | null {
  const latest = record.shipments[record.shipments.length - 1] ?? null;
  const parts = [
    record.transfer.logisticsMode?.name ?? (latest ? shipmentTitle(latest) : null),
    latest?.vehicle?.name ?? null,
  ].filter((part): part is string => Boolean(part));
  return parts.length ? Array.from(new Set(parts)).join(' · ') : null;
}

export type TransferStopKindOption = { value: 'pickup' | 'dropoff' | 'stop' | 'storage' | 'cross_dock'; label: string };

export const STOP_KINDS: ReadonlyArray<TransferStopKindOption> = [
  { value: 'stop', label: 'Stop' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'dropoff', label: 'Drop-off' },
  { value: 'storage', label: 'Storage' },
  { value: 'cross_dock', label: 'Cross-dock' },
];

export function stopKindLabel(kind: string | null | undefined): string {
  return STOP_KINDS.find((entry) => entry.value === kind)?.label ?? 'Stop';
}

const STOP_STATUS: { readonly [status: string]: { label: string; tone: StatusTone } } = {
  pending: { label: 'Expected', tone: 'neutral' },
  arrived: { label: 'Arrived', tone: 'progress' },
  available: { label: 'Available', tone: 'progress' },
  picked: { label: 'Picked', tone: 'progress' },
  shipped: { label: 'Left', tone: 'ok' },
  skipped: { label: 'Skipped', tone: 'stopped' },
};

export function stopStatus(status: string): { label: string; tone: StatusTone } {
  return STOP_STATUS[status] ?? { label: sentenceCaseStatus(status), tone: 'neutral' };
}

/**
 * The steps a stop can take next, as today's stop buttons offered them. "Mark available" moves the
 * transfer's shipped lines in transit, so on an R2 transfer (R2 owns its lines) an arrived stop goes
 * straight to picked or left instead.
 */
export function stopNextSteps(status: string, options: { r2?: boolean } = {}): Array<{ status: TransferStopProgress; label: string }> {
  switch (status) {
    case 'pending':
      return [{ status: 'arrived', label: 'Mark arrived' }];
    case 'arrived':
      return options.r2
        ? [{ status: 'picked', label: 'Mark picked' }, { status: 'shipped', label: 'Mark left' }]
        : [{ status: 'available', label: 'Mark available' }];
    case 'available':
      return [{ status: 'picked', label: 'Mark picked' }, { status: 'shipped', label: 'Mark left' }];
    case 'picked':
      return [{ status: 'shipped', label: 'Mark left' }];
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------------------------

/** The audit tables a transfer's History lists, with their tab names. */
export const TRANSFER_HISTORY_TABLES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'transfer_orders', label: 'Transfer' },
  { value: 'transfer_lines', label: 'Lines' },
  { value: 'transfer_waypoints', label: 'Route' },
];

/** Business events from the record's timeline, merged into History under this table name. */
export const TRANSFER_ACTIVITY_TABLE = 'transfer_order_events';

export function transferHistoryTab(tableName: string): string {
  if (tableName === TRANSFER_ACTIVITY_TABLE) return 'Activity';
  return TRANSFER_HISTORY_TABLES.find((table) => table.value === tableName)?.label
    ?? sentenceCaseStatus(tableName.replace(/^transfer_/, ''));
}

const HISTORY_FIELD_LABELS: { readonly [key: string]: string } = {
  doc_no: 'Transfer number',
  from_location_id: 'From site',
  to_location_id: 'To site',
  need_by_at: 'Needed by',
  expected_ship_at: 'Ships',
  expected_receive_at: 'Arrives',
  expected_return_at: 'Comes back',
  actual_ship_at: 'Shipped',
  actual_receive_at: 'Received',
  tracking_no: 'Tracking number',
  logistics_mode_id: 'Logistics mode',
  transfer_scope: 'Type',
  transfer_id: 'Transfer',
  transfer_line_id: 'Line',
  waypoint_id: 'Stop',
  qty: 'Quantity',
  picked_qty: 'Picked',
  shipped_qty: 'Shipped',
  received_qty: 'Received',
  kept_qty: 'Kept',
  short_qty: 'Short',
  ordered_qty: 'Ordered',
  external_item_name: 'Item name',
  stop_kind: 'Kind of stop',
  expected_arrive_at: 'Arrives',
  expected_depart_at: 'Leaves',
  actual_arrive_at: 'Arrived',
  actual_depart_at: 'Left',
};

export function transferHistoryFieldLabel(key: string): string {
  return HISTORY_FIELD_LABELS[key] ?? sentenceCaseStatus(key).replace(/\bid\b/gi, 'ID');
}

// ---------------------------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------------------------

export function eventLabel(event: Pick<TransferRecordEvent, 'code' | 'name'>): string {
  return [event.code, event.name].filter(Boolean).join(' - ') || 'Event';
}

// ---------------------------------------------------------------------------------------------
// Packing list
// ---------------------------------------------------------------------------------------------

export function packingListInputFor(record: TransferRecord, now: Date = new Date()): PackingListInput {
  const { transfer } = record;
  const fromZone = transfer.fromSite?.timezone ?? null;
  const toZone = transfer.toSite?.timezone ?? null;
  return {
    docNo: transfer.docNo ?? transfer.id.slice(0, 8),
    title: transfer.description,
    fromSite: siteName(transfer.fromSite, 'Shipping site'),
    toSite: siteName(transfer.toSite, 'Receiving site'),
    scopeLabel: scopeLabel(transfer, now),
    needBy: formatSiteDateTime(transfer.needByAt, toZone, now),
    ships: formatSiteDateTime(transfer.actualShipAt ?? transfer.expectedShipAt, fromZone, now),
    arrives: formatSiteDateTime(transfer.actualReceiveAt ?? transfer.expectedReceiveAt, toZone, now),
    logistics: logisticsSummary(record),
    events: record.events.map(eventLabel),
    lines: groupLinesByCategory(record.lines.filter((line) => line.status !== 'cancelled')).lines.map((line) => ({
      name: lineDisplayName(line),
      sku: lineSku(line),
      category: lineCategory(line),
      qty: line.qty,
      serials: lineSerialCodes(line),
      tracking: line.tracking,
      notes: line.notes,
    })),
    printedAt: now.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }),
    autoPrint: true,
  };
}
